import * as React from "react";
import { EmptyState } from "@ardept/design-system";
import { readServerRefusal } from "../api/serverRefusal.js";
import type {
  OperatorOutstandingDto,
  OperatorOutstandingItemDto,
  OperatorOutstandingItemDecisionRequest,
} from "../api/types.js";

/**
 * Phase 529 wave 6 task 6.2 (card #1941, AC-6) — what this file still owes, on the operator
 * job detail: each item by its DECLARED label, with its status, who owes it and how long it
 * has been owed.
 *
 * <b>Why a component of its own rather than more JSX inside JobDetailScreen.</b> Two reasons,
 * and both are about proof. (1) The screen is a hooks-and-router component that cannot be
 * rendered outside a QueryClient and a Router, so anything living inside it can only ever be
 * proven by grepping its source — and a source-grep cannot tell you what a reader SEES. Lifted
 * out, this is a pure function of props, which the sibling test renders through
 * `react-dom/server` and asserts over the ACTUAL markup. (2) The portal's onboarding board
 * (#1939) is a second consumer of the same read model; the next surface reuses this instead of
 * re-deciding how a waive reads.
 *
 * <b>The vocabulary is the ENGINE's, and stays there.</b> `status` and `owner` are members of
 * the closed `job_outstanding_items` vocabularies (`OutstandingItemVocabulary`, fenced against
 * the migration) and are rendered RAW. The console holds no second copy and no prose map: a
 * label table here would be a mirror to keep in step with a CHECK constraint, which is the
 * exact drift class (L-43) this phase exists to close. `kind` and `subject` are further still
 * from the platform — they are a TENANT's own declaration vocabulary, so the only copy this
 * file renders for an item is the declared `label` the engine persisted on the row (AC-2). No
 * client name, no reference name and no reference VALUE can reach this component, because the
 * DTO has no key for one.
 */

/**
 * Phase 544 wave 4 task 4.1 (card #1990, AC-5) — the panel also RECORDS now, and the two
 * affordances it offers are decided by the DECLARATION, not by this file.
 *
 * <b>Every open item can be marked "Not required" (reason required); ONLY a `settled_by: staff`
 * item can additionally be "Record[ed] as received".</b> Both are 529's existing acts reached
 * through ONE engine route (`POST /internal/v0/jobs/{id}/outstanding-items/decisions`), which maps
 * the outcome onto `waive_outstanding_item` / `close_outstanding_item`. The asymmetry is the
 * engine's rule, not a UI preference: a staff "received" on a `settled_by: value` row would close
 * it with no value anywhere in ctx and open the downstream push gate on a file that is still
 * missing the reference. The engine refuses that by name (`outstanding_item_not_staff_settled`),
 * and this panel simply does not offer the button the engine would refuse — the rule lives in one
 * place and the screen agrees with it by reading `settled_by`, never by re-deriving it from
 * `owner` (which answers a different question: WHO is chased, not WHAT settles it).
 *
 * <b>GROUND TRUTH about the APPROVE button, stated here because it is what makes the held-approval
 * half of this phase need no screen change at all.</b> `JobDetailScreen`'s Approve/Reject buttons
 * are gated on the job being in `PENDING_REVIEW` (`isPending`), and its `DecisionErrorBand` already
 * renders ANY engine refusal through `readServerRefusal` — which reads `{error_code, message}`
 * generically. So when wave 3's declared hold refuses an approval with 409
 * `approval_held_outstanding`, the practice's OWN declared sentence renders verbatim in the
 * existing band with no new branch, no new copy and no new state. `test/decision-refusal.test.ts`
 * proves it with a 409 case rather than leaving it as a claim.
 *
 * <b>Why the mutation is INJECTED rather than called here.</b> This component is a pure function of
 * its props — that is what lets its sibling test render the REAL tree through `react-dom/server` in
 * plain node and assert what a staffer SEES. Calling `usePostOutstandingItemDecision` (and through
 * it `useMutation`) inside would require a `QueryClientProvider` around every render and would put
 * this panel back where task 6.2 lifted it out of. `JobDetailScreen` owns the hook; the panel
 * receives `onDecide`, and a panel given none stays exactly the read-only surface 529 shipped.
 */

/** Props — the two route-scoped members of `OperatorJobDto`, passed straight through. */
export interface OutstandingItemsPanelProps {
  /**
   * The engine's summary. UNDEFINED means the route did not carry the answer (the jobs LIST
   * omits it), and the panel then renders NOTHING — absence is not "clear", and a panel that
   * treated it as clear would tell a staffer a file was free to push on the strength of a key
   * that was never sent.
   */
  outstanding?: OperatorOutstandingDto;
  /** Every item, in every status, oldest first — the engine's order, preserved. */
  items?: OperatorOutstandingItemDto[];
  /**
   * Injected clock, so the age a reader sees is a pure function of props in the test. Defaults
   * to now in the browser.
   */
  now?: number;
  /**
   * Phase 544 — record an item. ABSENT means this surface cannot record, and then NO item offers
   * an affordance: a button with nowhere to send its answer is worse than no button. The panel
   * never decides WHAT the record does — it hands the engine the row's identity and the OUTCOME
   * the operator chose.
   */
  onDecide?: (request: OperatorOutstandingItemDecisionRequest) => void;
  /** True while a record is in flight — every affordance on the panel disables, because the two
   *  acts are terminal on a row and a double-submit would earn a named refusal. */
  decisionPending?: boolean;
  /** The engine's refusal from the last attempt, rendered VERBATIM through `readServerRefusal`:
   *  it names which act this row admits, which no sentence of the console's could. */
  decisionError?: unknown;
}

/**
 * How long an item has been owed, in the coarsest unit that is still true. Deliberately NOT a
 * date: "how long has this been outstanding" is the question a staffer actually asks, and a
 * timestamp makes them do the subtraction.
 *
 * An unparseable `since` returns "an unknown length of time" rather than a number derived from
 * NaN — the whole point of the ledger is that the screen stops guessing (AC-5).
 */
export function formatOwedFor(sinceIso: string, now: number): string {
  const startedAt = new Date(sinceIso).getTime();
  if (!Number.isFinite(startedAt)) return "an unknown length of time";
  const ms = now - startedAt;
  if (ms < 60_000) return "under a minute";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return plural(minutes, "minute");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return plural(hours, "hour");
  return plural(Math.floor(hours / 24), "day");
}

/**
 * Is the declared deadline in the past? Only ever asked of an item that HAS one — a declaration
 * that names no `due_after` omits `due_at` entirely, and an absent deadline is not a missed one.
 */
export function isOverdue(dueAtIso: string, now: number): boolean {
  const dueAt = new Date(dueAtIso).getTime();
  return Number.isFinite(dueAt) && dueAt < now;
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
}

/** "DD Mon YYYY" in UTC — the console's existing convention for a date without a clock time. */
function formatDayUTC(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Phase 544 wave 4 task 4.1 — the two affordances for ONE open ledger row.
 *
 * <b>A component of its own because the inputs need state PER ROW.</b> A reason typed against one
 * item must not appear under another, and hooks cannot be called inside a `map`. It is still a pure
 * function of props plus its own local draft, so the sibling test renders it through
 * `react-dom/server` exactly as it renders the panel.
 *
 * <b>The copy is the item's DECLARED `subject`.</b> Every sentence here is either the engine's own
 * closed vocabulary or the workflow author's label — the console names no reference, no tax term
 * and no client (the derived sweep in `test/outstanding-items-copy.fence.test.ts` proves it over
 * the whole of `src/`).
 */
function OutstandingItemActions({
  item,
  onDecide,
  pending,
}: {
  item: OperatorOutstandingItemDto;
  onDecide: (request: OperatorOutstandingItemDecisionRequest) => void;
  pending: boolean;
}): JSX.Element {
  const [reason, setReason] = React.useState("");
  const [note, setNote] = React.useState("");

  // The row's whole identity, exactly as the ledger keys it: `item_key` alone does not address a
  // row, because two forms may each declare the same item id for the same party. `form_id` is
  // non-null here by construction — the caller only renders this for a LEDGER row.
  const target = {
    form_id: item.form_id!,
    item_key: item.kind,
    iteration_key: item.iteration_key,
  };

  // A reason of whitespace is not a reason — the same test the engine applies before it opens a
  // transaction, applied here so the operator learns it without a round-trip.
  const reasonGiven = reason.trim().length > 0;

  return (
    <div className="outstanding__actions">
      {/* ONLY on a staff-settled item. See the file header: this is the engine's rule, read off
          the declaration, not a preference of this screen's. */}
      {item.settled_by === "staff" && (
        <div className="outstanding__action">
          <label className="outstanding__action-label">
            Note (optional)
          </label>
          <input
            type="text"
            className="outstanding__action-input"
            value={note}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNote(e.target.value)
            }
            disabled={pending}
            placeholder="How it arrived, for the record."
          />
          <button
            type="button"
            className="outstanding__action-btn"
            disabled={pending}
            onClick={() =>
              onDecide({
                ...target,
                decision: "received",
                // Omitted rather than sent blank: a reader who finds the key at all is reading
                // words a person actually wrote.
                ...(note.trim().length > 0 ? { note: note.trim() } : {}),
              })
            }
          >
            Record as received
          </button>
        </div>
      )}

      {/* EVERY open item. A waive is how a file is accepted without the item, so the reason is
          mandatory — and the button stays disabled until there is one, rather than letting the
          operator discover it from a 400. */}
      <div className="outstanding__action">
        <label className="outstanding__action-label">Reason (required)</label>
        <input
          type="text"
          className="outstanding__action-input"
          value={reason}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setReason(e.target.value)
          }
          disabled={pending}
          placeholder="Why this file can be accepted without it."
        />
        <button
          type="button"
          className="outstanding__action-btn"
          disabled={pending || !reasonGiven}
          onClick={() =>
            onDecide({ ...target, decision: "not_required", reason: reason.trim() })
          }
        >
          Not required
        </button>
      </div>
    </div>
  );
}

export function OutstandingItemsPanel({
  outstanding,
  items,
  now = Date.now(),
  onDecide,
  decisionPending = false,
  decisionError,
}: OutstandingItemsPanelProps): JSX.Element | null {
  // The route did not carry the answer. Say nothing at all.
  if (!outstanding) return null;

  const rows = items ?? [];

  return (
    <section className="outstanding" aria-label="Outstanding items">
      <div className="panel-head">
        <span>Outstanding</span>
        {/* Read off the ENGINE's own `clear` / `open_count`, never recomputed from `rows` —
            the same two numbers the staff agent reads and the same ones the engine's
            pre-push precondition enforces, so the screen and the gate cannot disagree. */}
        <span className="meta">
          {outstanding.clear
            ? "Nothing outstanding"
            : `${outstanding.open_count} still owed`}
        </span>
      </div>

      {rows.length === 0 ? (
        // The SHARED empty state, not a sentence of this screen's own (AC-5).
        <EmptyState
          iconName="check-circle-2"
          title="Nothing outstanding"
          body="This file owes nothing and is not held from a downstream push."
        />
      ) : (
        <ul className="outstanding__list">
          {rows.map((item) => (
            <li
              // Phase 529 wave 10 task 10.6 — the ledger row's WHOLE identity, in the engine's
              // own order: `(form_id, item_key, iteration_key)`. `kind` alone is not unique
              // across declaring forms, so two forms each declaring the same supply-later id
              // for the same party would otherwise share a key and React would treat them as
              // one row. `?? ""` is the party arm, which has no declaring form and cannot
              // collide with a ledger row (its `kind` is `party_<status>`).
              key={`${item.form_id ?? ""}|${item.kind}|${item.iteration_key}`}
              className="outstanding__item"
            >
              <div className="outstanding__item-head">
                {/* The DECLARED label. `kind` is the fallback ONLY because the party arm's
                    display name is genuinely nullable — an honest engine token beats an
                    invented name. */}
                <span className="outstanding__subject">
                  {item.subject ?? item.kind}
                </span>
                <span
                  className={`outstanding__status outstanding__status--${item.status}`}
                >
                  {item.status}
                </span>
              </div>
              <div className="outstanding__item-meta">
                <span className="outstanding__owner">Owed by {item.owner}</span>
                <span className="outstanding__age">
                  {item.status === "open"
                    ? `Owed for ${formatOwedFor(item.since, now)}`
                    : `Opened ${formatOwedFor(item.since, now)} ago`}
                </span>
                {item.due_at !== undefined && (
                  <span
                    className={
                      isOverdue(item.due_at, now)
                        ? "outstanding__due outstanding__due--overdue"
                        : "outstanding__due"
                    }
                  >
                    {isOverdue(item.due_at, now)
                      ? `Overdue since ${formatDayUTC(item.due_at)}`
                      : `Due ${formatDayUTC(item.due_at)}`}
                  </span>
                )}
              </div>
              {/* A waive is the compliance-bearing half: WHO accepted the file without the item
                  and WHY, in their own words. Each half is guarded separately so a half-written
                  row can never render an attribution with empty quotes after it. */}
              {item.waived_by !== undefined && (
                <p className="outstanding__waive">
                  Waived by {item.waived_by}
                  {item.waive_reason !== undefined && (
                    <span className="outstanding__waive-reason">
                      {" "}
                      &mdash; &ldquo;{item.waive_reason}&rdquo;
                    </span>
                  )}
                </p>
              )}
              {/* Phase 544 — recordable only while the item is STILL OPEN, and only on the ledger
                  arm. A settled row is terminal (the engine answers 409
                  `outstanding_item_already_settled`), and the party arm has no declaring form, so
                  there is no ledger row for either act to address — its `form_id` is absent and
                  `status` is `open` by construction, which is why BOTH guards are needed. */}
              {onDecide !== undefined &&
                item.status === "open" &&
                item.form_id !== undefined && (
                  <OutstandingItemActions
                    item={item}
                    onDecide={onDecide}
                    pending={decisionPending}
                  />
                )}
            </li>
          ))}
        </ul>
      )}

      {/* The engine's refusal, in the engine's own words. `readServerRefusal` returns null for
          anything that is not an engine refusal (a transport failure, a 5xx with an HTML body), and
          the panel then says the one generic sentence that was ever true of those. */}
      {decisionError !== undefined && decisionError !== null && (
        <p className="outstanding__error" role="alert">
          {readServerRefusal(decisionError)?.message ??
            "Could not record this item. Check the job status and try again."}
        </p>
      )}
    </section>
  );
}

export default OutstandingItemsPanel;
